'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'

/**
 * Open/closed state for the feature flag panel, held outside React because its
 * two entry points sit in unrelated subtrees: the keyboard chord lives with the
 * panel in `FeatureFlagsLauncher`, and the Cmd+K command lives in
 * `QuickSearchBar`. A module store keeps them in sync without threading a
 * provider through the layout or adding a persisted Redux slice for state that
 * should not survive a reload.
 */
let isOpen = false
const listeners = new Set<() => void>()

function setOpen(next: boolean) {
  if (isOpen === next) return
  isOpen = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): boolean {
  return isOpen
}

function getServerSnapshot(): boolean {
  return false
}

export function openFeatureFlagsPanel() {
  setOpen(true)
}

export type UseFeatureFlagsPanelResult = {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

export default function useFeatureFlagsPanel(): UseFeatureFlagsPanelResult {
  const open = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])
  const handleToggle = useCallback(() => setOpen(!isOpen), [])

  return useMemo(
    () => ({ isOpen: open, open: handleOpen, close: handleClose, toggle: handleToggle }),
    [open, handleOpen, handleClose, handleToggle]
  )
}
